import { useMutation } from "@apollo/client";
import {
  Button,
  Group,
  Modal,
  Rating,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { UserType } from "../../../../gql/__generated__/graphql";
import { CREATE_REVIEW } from "../../../apis/lambda/GraphQL/Review/mutations";

interface ReviewModalProps {
  opened: boolean;
  onClose: () => void;
  entityType: UserType;
  entityName: string; // Name of the entity being reviewed
  entityId: string; // ID of the entity being reviewed
}

function ReviewModal({
  opened,
  onClose,
  entityType,
  entityName,
  entityId: revieweeId,
}: ReviewModalProps) {
  // React State
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createReview] = useMutation(CREATE_REVIEW);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await createReview({
        variables: {
          input: {
            revieweeId,
            rating,
            comment: reviewText,
          },
        },
      });
      notifications.show({
        title: "Review Submitted",
        message: "Thank you for your feedback!",
        color: "green",
      });
      onClose(); // Close the modal after submission
    } catch (error) {
      console.error("Error submitting review:", error);
      notifications.show({
        title: "Error",
        message: "Failed to submit review. Please try again.",
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setRating(0);
      setReviewText("");
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={<Title order={3}>Leave a Review</Title>}
      size="md"
      centered
    >
      <Stack gap="md">
        <Text>
          Please leave a review for this{" "}
          {entityType === "CREATOR" ? "Creator" : "Brand"}:{" "}
          <Text span fw={700}>
            {entityName}
          </Text>
        </Text>

        <Rating value={rating} onChange={setRating} size="xl" count={5} />

        <Textarea
          placeholder="Share your experience..."
          value={reviewText}
          onChange={(event) => setReviewText(event.currentTarget.value)}
          minRows={4}
          maxRows={6}
        />

        <Group justify="flex-end">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={rating === 0 || isSubmitting}
            loading={isSubmitting}
          >
            Submit Review
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default ReviewModal;
